// src/pages/RetreatCalendarPage.js
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  CalendarDays,
  MapPin,
  Clock3,
  Users,
  ArrowRight,
  Info,
  List
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { KeyUri } from '../key';

const getDaysInMonth = (month, year) => {
  const date = new Date(year, month, 1);
  const days = [];
  while (date.getMonth() === month) {
    days.push(new Date(date));
    date.setDate(date.getDate() + 1);
  }
  return days;
};

const formatDateKey = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatMonthKey = (year, month) => {
  return `${year}-${String(month + 1).padStart(2, '0')}`;
};

const parseMonthKey = (monthKey) => {
  const [year, month] = monthKey.split('-').map(Number);
  return { year, month: month - 1 };
};

const isSameOrAfterToday = (date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d >= today;
};

const getRetreatStatus = (schedule) => {
  const remaining = (schedule.totalSlots || 0) - (schedule.bookedSlots || 0);
  if (remaining <= 0) return 'soldout';
  if (remaining <= 3) return 'high';
  return 'available';
};

const RetreatCard = ({ item, onViewDetails }) => {
  const pkg = item.wellnessPackage;
  const remaining = (item.totalSlots || 0) - (item.bookedSlots || 0);
  const status = getRetreatStatus(item);

  
  console.log("program_only:", pkg?.program_only, "base_program_price:", pkg?.base_program_price);
  return (
    <div className="border border-slate-200 rounded-2xl bg-white overflow-hidden shadow-sm hover:shadow-md transition-all duration-300">
      {pkg?.image?.[0] ? (
        <img
          src={pkg.image[0]}
          alt={pkg.title}
          className="w-full h-48 object-cover"
        />
      ) : (
        <div className="w-full h-48 bg-slate-100 flex items-center justify-center text-slate-400">
          No image
        </div>
      )}

      <div className="p-5">
      <div className="mb-3">
  {/* Badge on top */}
  <div className="mb-2">
    {status === 'soldout' ? (
      <span className="inline-flex px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide rounded-full bg-slate-100 text-slate-600 whitespace-nowrap">
        Sold Out
      </span>
    ) : status === 'high' ? (
      <span className="inline-flex px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide rounded-full bg-amber-100 text-amber-700 whitespace-nowrap">
        High Demand
      </span>
    ) : (
      <span className="inline-flex px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide rounded-full bg-emerald-50 text-emerald-700 whitespace-nowrap">
        Available
      </span>
    )}
  </div>

  {/* Title + Location below */}
  <h3 className="text-base md:text-lg font-bold text-slate-900 leading-snug">{pkg?.title}</h3>
  <div className="flex items-center text-sm text-slate-500 mt-1">
    <MapPin size={14} className="mr-1 flex-shrink-0" />
    <span className="truncate">
      {pkg?.location?.city || 'Unknown city'}
      {pkg?.location?.state ? `, ${pkg.location.state}` : ''}
    </span>
  </div>
</div>

       <p
  className="text-sm text-slate-600 line-clamp-2 mb-4"
  dangerouslySetInnerHTML={{
    __html: pkg?.description || 'No description available.'
  }}
/>

        <div className="grid grid-cols-2 gap-3 text-sm mb-4">
          <div className="flex items-center text-slate-600">
            <Clock3 size={15} className="mr-2 text-slate-400" />
            <span>{pkg?.duration || 'N/A'}</span>
          </div>

          {item.show_available_slots && (
  <div className="flex items-center text-slate-600">
    <Users size={15} className="mr-2 text-slate-400" />
    <span>{remaining} slots left</span>
  </div>
)}

          <div className="flex items-center text-slate-600 col-span-2">
            <CalendarDays size={15} className="mr-2 text-slate-400" />
            <span>
              {new Date(item.startDate).toLocaleDateString('en-IN', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              })}{' '}
              -{' '}
              {new Date(item.endDate).toLocaleDateString('en-IN', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              })}
            </span>
          </div>
        </div>

        <div className="flex items-end justify-between gap-3">
          <div>
           <div className="text-xl font-bold text-slate-900">
  ₹{Number(pkg?.program_only?.display_price || pkg?.base_program_price || 0).toLocaleString('en-IN')}
</div>
{pkg?.program_only?.original_display_price ? (
  <div className="text-sm text-slate-400 line-through">
    ₹{Number(pkg.program_only.original_display_price).toLocaleString('en-IN')}
  </div>
) : null}
          </div>

        <button
  onClick={() => onViewDetails(pkg?._id, item?._id)}
  disabled={!pkg?._id}
  className="inline-flex items-center px-3 py-1.5 md:px-4 md:py-2.5 text-xs md:text-sm rounded-lg md:rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:bg-slate-300"
>
  <span className="hidden md:inline">View Retreat</span>
  <span className="md:hidden">View </span>
  <ArrowRight size={14} className="ml-1 md:ml-2" />
</button>
        </div>
      </div>
    </div>
  );
};

const RetreatCalendarPage = () => {
  const navigate = useNavigate();
  const now = new Date();

  const [currentMonth, setCurrentMonth] = useState(now.getMonth());
  const [currentYear, setCurrentYear] = useState(now.getFullYear());
  const [selectedDate, setSelectedDate] = useState(now);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewingCount, setViewingCount] = useState(4);
  const [viewMode, setViewMode] = useState('calendar');
  const [expandedMonthKey, setExpandedMonthKey] = useState(
    formatMonthKey(now.getFullYear(), now.getMonth())
  );
const [monthCounts, setMonthCounts] = useState({});

  const days = useMemo(
    () => getDaysInMonth(currentMonth, currentYear),
    [currentMonth, currentYear]
  );

  const monthList = useMemo(() => {
    const list = [];
    const start = new Date(now.getFullYear(), now.getMonth(), 1);

    for (let i = 0; i < 12; i++) {
      const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
      list.push({
        key: formatMonthKey(d.getFullYear(), d.getMonth()),
        month: d.getMonth(),
        year: d.getFullYear(),
        label: d.toLocaleDateString('en-IN', {
          month: 'long',
          year: 'numeric'
        })
      });
    }

    return list;
  }, [now]);

  useEffect(() => {
    const interval = setInterval(() => {
      setViewingCount((prev) => {
        const delta = Math.random() > 0.5 ? 1 : -1;
        return Math.max(1, Math.min(9, prev + delta));
      });
    }, 8000);

    return () => clearInterval(interval);
  }, []);

const fetchCalendarData = async (year, month) => {
  try {
    setLoading(true);
    const monthParam = `${year}-${String(month + 1).padStart(2, '0')}`;
    const res = await axios.get(
      `${KeyUri.BACKEND_URI}/retreat-schedules/calendar?month=${monthParam}`
    );

    const data = res.data || [];
    setSchedules(data);

    const key = formatMonthKey(year, month);
    setMonthCounts((prev) => ({
      ...prev,
      [key]: data.length
    }));
  } catch (error) {
    console.error('Failed to fetch calendar data:', error);
    setSchedules([]);

    const key = formatMonthKey(year, month);
    setMonthCounts((prev) => ({
      ...prev,
      [key]: 0
    }));
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    if (viewMode === 'calendar') {
      fetchCalendarData(currentYear, currentMonth);
    }
  }, [currentMonth, currentYear, viewMode]);

  useEffect(() => {
    if (viewMode === 'list' && expandedMonthKey) {
      const { year, month } = parseMonthKey(expandedMonthKey);
      fetchCalendarData(year, month);
    }
  }, [expandedMonthKey, viewMode]);

  const groupedByDate = useMemo(() => {
    return schedules.reduce((acc, item) => {
      const key = formatDateKey(item.startDate);
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});
  }, [schedules]);

  const sortedSchedules = useMemo(() => {
    return [...schedules].sort(
      (a, b) => new Date(a.startDate) - new Date(b.startDate)
    );
  }, [schedules]);

  const selectedDateKey = formatDateKey(selectedDate);
  const selectedRetreats = groupedByDate[selectedDateKey] || [];

  const changeMonth = (direction) => {
    if (direction === 'prev') {
      if (currentMonth === 0) {
        setCurrentMonth(11);
        setCurrentYear((prev) => prev - 1);
      } else {
        setCurrentMonth((prev) => prev - 1);
      }
    } else {
      if (currentMonth === 11) {
        setCurrentMonth(0);
        setCurrentYear((prev) => prev + 1);
      } else {
        setCurrentMonth((prev) => prev + 1);
      }
    }
  };

  const handleViewDetails = (packageId, scheduleId) => {
    navigate(`/wellness/${packageId}?scheduleId=${scheduleId}`, {
      state: { scheduleId }
    });
  };

  const handleMonthToggle = (monthKey) => {
    const { year, month } = parseMonthKey(monthKey);
    setExpandedMonthKey((prev) => (prev === monthKey ? null : monthKey));
    setCurrentYear(year);
    setCurrentMonth(month);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-20 mt-20 md:mt-28">
      <header className="bg-white border-b border-slate-200 px-4 sm:px-6 py-8 md:py-12">
        <div className="max-w-7xl mx-auto">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-sm font-medium mb-4">
            <CalendarDays size={16} className="mr-2" />
            Fixed-date wellness retreats
          </div>

          <h1 className="text-3xl md:text-5xl font-bold text-slate-900 tracking-tight max-w-3xl">
            Choose your date and explore all available wellness retreats
          </h1>

          <p className="text-slate-500 mt-4 max-w-2xl text-base md:text-lg">
            Browse all upcoming retreats by date, compare options, and open the retreat that fits your plan best.
          </p>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <section className={viewMode === 'calendar' ? 'lg:col-span-7 space-y-5' : 'lg:col-span-12 space-y-5'}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-slate-900">Browse Retreat Dates</h2>
              <p className="text-sm text-slate-500 mt-1">
                Switch between calendar and list view
              </p>
            </div>

            <div className="flex bg-slate-100 p-1 rounded-xl border border-slate-200 w-fit">
              <button
                onClick={() => setViewMode('calendar')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                  viewMode === 'calendar'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                <CalendarDays size={16} />
                <span className="hidden sm:inline">Calendar</span>
              </button>

              <button
                onClick={() => setViewMode('list')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                  viewMode === 'list'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                <List size={16} />
                <span className="hidden sm:inline">List View</span>
              </button>
            </div>
          </div>

          

          {viewMode === 'calendar' ? (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 md:p-6">
              <div className="flex items-center justify-between mb-6">
                <button
                  onClick={() => changeMonth('prev')}
                  className="p-2 border border-slate-200 rounded-xl hover:bg-slate-50 text-slate-600"
                >
                  <ChevronLeft size={20} />
                </button>

                <h2 className="text-lg md:text-xl font-bold text-slate-900">
                  {new Date(currentYear, currentMonth).toLocaleString('default', {
                    month: 'long',
                    year: 'numeric'
                  })}
                </h2>

                <button
                  onClick={() => changeMonth('next')}
                  className="p-2 border border-slate-200 rounded-xl hover:bg-slate-50 text-slate-600"
                >
                  <ChevronRight size={20} />
                </button>
              </div>

              <div className="grid grid-cols-7 gap-1 mb-2 text-center text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
  {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
    <div key={i} className="py-1">{d}</div>
  ))}
</div>

              <div className="grid grid-cols-7 gap-2">
                {Array(days[0].getDay()).fill(null).map((_, i) => (
                 <div key={`empty-${i}`} className="min-h-[56px] md:h-24" />
                ))}

                {days.map((day) => {
                  const key = formatDateKey(day);
                  const daySchedules = groupedByDate[key] || [];
                  const isSelected = key === selectedDateKey;
                  const isPast = !isSameOrAfterToday(day);

                  let tileClass =
                    'border-slate-200 bg-white hover:border-emerald-300 hover:bg-emerald-50/40 text-slate-800';

                  if (isSelected) {
                    tileClass = 'border-emerald-500 bg-emerald-50 text-emerald-800';
                  } else if (isPast) {
                    tileClass = 'border-slate-100 bg-slate-50 text-slate-300';
                  } else if (daySchedules.length === 0) {
                    tileClass = 'border-slate-100 bg-slate-50 text-slate-400';
                  }

              return (
   <button
  key={key}
  onClick={() => setSelectedDate(day)}
  className={`min-h-[56px] md:h-24 rounded-xl md:rounded-2xl border p-1 md:p-2 text-left transition-all duration-200 ${tileClass}`}
>
  <div className="text-[10px] md:text-sm font-bold leading-tight">{day.getDate()}</div>

  {daySchedules.length > 0 && (
    <div className="mt-0.5 space-y-0.5">
      <div className="text-[8px] md:text-[11px] font-semibold text-emerald-700 leading-tight break-words">
        {daySchedules.length} retreat{daySchedules.length > 1 ? 's' : ''}
      </div>

      <div className="flex gap-0.5 flex-wrap">
        {daySchedules.slice(0, 3).map((item) => {
          const status = getRetreatStatus(item);
          return (
            <span
              key={item._id}
              className={`h-1 w-1 md:h-1.5 md:w-1.5 rounded-full flex-shrink-0 ${
                status === 'soldout'
                  ? 'bg-slate-300'
                  : status === 'high'
                  ? 'bg-amber-500'
                  : 'bg-emerald-500'
              }`}
            />
          );
        })}
      </div>
    </div>
  )}
</button>
    );
  })}
</div>

              <div className="flex flex-wrap items-center gap-4 mt-6 pt-5 border-t border-slate-100 text-xs text-slate-500">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-emerald-500" />
                  <span>Available</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-amber-500" />
                  <span>High demand</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-slate-300" />
                  <span>Sold out</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {monthList.map((monthItem) => {
                const isExpanded = expandedMonthKey === monthItem.key;

                return (
                  <div
                    key={monthItem.key}
                    className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden"
                  >
                    <button
                      onClick={() => handleMonthToggle(monthItem.key)}
                      className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors"
                    >
                      <div>
                        <h3 className="text-lg md:text-xl font-bold text-slate-900">
                          {monthItem.label}
                        </h3>
    <p className="text-sm text-slate-500 mt-1">
  {monthCounts[monthItem.key] !== undefined
    ? `${monthCounts[monthItem.key]} retreat batch${monthCounts[monthItem.key] !== 1 ? 'es' : ''} available`
    : 'Tap to load retreats'}
</p>
                      </div>

                      <ChevronDown
                        size={20}
                        className={`text-slate-500 transition-transform duration-200 ${
                          isExpanded ? 'rotate-180' : ''
                        }`}
                      />
                    </button>

                    {isExpanded && (
                      <div className="border-t border-slate-100 p-5 bg-slate-50">
                        {loading ? (
                          <div className="rounded-2xl border border-slate-200 bg-white p-5 text-slate-500">
                            Loading retreats...
                          </div>
                        ) : sortedSchedules.length === 0 ? (
                          <div className="rounded-2xl border border-slate-200 bg-white p-5">
                            <div className="flex items-start gap-3 text-slate-600">
                              <Info size={18} className="mt-0.5 text-slate-400" />
                              <div>
                                <div className="font-semibold text-slate-800">No retreats this month</div>
                                <p className="text-sm mt-1">
                                  Try another month to see upcoming retreat batches.
                                </p>
                              </div>
                            </div>
                          </div>
                        ) : (
                         <div className="flex gap-5 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory">
  {sortedSchedules.map((item) => (
    <div key={item._id} className="flex-shrink-0 w-[300px] snap-start">
      <RetreatCard
        item={item}
        onViewDetails={handleViewDetails}
      />
    </div>
  ))}
</div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {viewMode === 'calendar' && (
          <section className="lg:col-span-5">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 md:p-6 sticky top-6">
              <div className="mb-5">
                <h2 className="text-xl font-bold text-slate-900">
                  Retreats on{' '}
                  {selectedDate.toLocaleDateString('en-IN', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </h2>

                <p className="text-sm text-slate-500 mt-1">
                  {selectedRetreats.length} option{selectedRetreats.length !== 1 ? 's' : ''} available
                </p>
              </div>

              {loading ? (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 text-slate-500">
                  Loading retreats...
                </div>
              ) : selectedRetreats.length === 0 ? (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                  <div className="flex items-start gap-3 text-slate-600">
                    <Info size={18} className="mt-0.5 text-slate-400" />
                    <div>
                      <div className="font-semibold text-slate-800">No retreats on this date</div>
                      <p className="text-sm mt-1">
                        Try another date in this month to see available retreat batches.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
                  {selectedRetreats.map((item) => (
                    <RetreatCard
                      key={item._id}
                      item={item}
                      onViewDetails={handleViewDetails}
                    />
                  ))}
                </div>
              )}
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default RetreatCalendarPage;